/**
 * useCurrentSubscription — single source of truth for the org's current sub.
 *
 * Why this exists: before consolidation, 8 different components each wrote
 * their own `useQuery({ queryKey: 'current-subscription', ... })` block.
 * The keys were INCONSISTENT — some used `['current-subscription']`, some
 * used `['current-subscription', organizationId]` — so React Query saw
 * them as separate queries and fired 4-6 parallel identical HTTP requests
 * on every page load (visible in DevTools Network tab as duplicate
 * `/subscriptions/current` calls).
 *
 * This hook centralises the query shape so every caller shares one cache
 * entry. First component to mount fires the fetch; everyone else reads
 * from cache for the next `staleTime` window, zero extra network.
 *
 * Semantics preserved from the previous per-component copies:
 *   - "No active subscription found" from the API is a VALID state
 *     (trial users, new orgs). Returned as `null`, NOT thrown.
 *   - Other errors throw so the caller can show an error UI.
 *   - Disabled by default when no user is loaded — prevents firing before
 *     auth resolves.
 *
 * Invalidation: any code that mutates subscription state (plan change,
 * trial upgrade, webhook-driven status flip) should call
 * `queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_KEY })`
 * so all mounted readers re-fetch on their next render.
 */

import { useQuery, keepPreviousData, type UseQueryOptions } from '@tanstack/react-query';
import { subscriptionsApi } from '@/lib/api/services/subscriptions';
import type { Subscription } from '@/lib/api/services/subscriptions';

// Canonical query key — every consumer MUST import this, not inline the string.
// A typo = a cache miss = duplicate fetch. Named export so CI grep catches drift.
export const CURRENT_SUBSCRIPTION_KEY = ['current-subscription'] as const;

type HookOptions = {
  /**
   * Whether to fetch at all. Components that only render for super admins
   * / agency users / unauthenticated states should pass `enabled: false`
   * to keep the query dormant.
   */
  enabled?: boolean;

  /**
   * Override stale time for callers that need stricter freshness
   * (e.g. a billing page that just completed a checkout and wants to
   * see the subscription flip to active). Defaults to 2 minutes —
   * a compromise between the old values (30s and 5min) across call sites.
   */
  staleTime?: UseQueryOptions['staleTime'];

  /**
   * Polling interval. Used by TopNavigation to re-check every 15s while
   * a subscription is in `pending` status (waiting for a payment webhook
   * to flip it to active). Supply a function that returns a number of ms
   * to poll, or `false` to stop. See TopNavigation.tsx for the canonical
   * pattern.
   *
   * Because all consumers share one query entry, whichever mounted caller
   * sets the highest-freshness interval wins for everyone — which is fine,
   * since the data is identical across consumers.
   */
  refetchInterval?: UseQueryOptions<Subscription | null>['refetchInterval'];
};

export function useCurrentSubscription(opts: HookOptions = {}) {
  const { enabled = true, staleTime = 2 * 60 * 1000, refetchInterval } = opts;

  return useQuery<Subscription | null>({
    queryKey: CURRENT_SUBSCRIPTION_KEY,
    queryFn: async () => {
      const result = await subscriptionsApi.getCurrentSubscription();
      // Normalise the two legit "no-sub" signals into the same shape:
      //   (a) apiClient returned `{ error: 'No active subscription found' }`
      //   (b) apiClient returned `{ data: null, error: null }`
      // Either way, caller sees `null` — NOT an exception, NOT undefined.
      const err = (result as any)?.error;
      if (err && err === 'No active subscription found') return null;
      if (err) throw new Error(err);
      return result.data ?? null;
    },
    enabled,
    staleTime,
    // Keep the last good subscription on screen across transient refetch errors
    // (token-refresh races, 401s, network blips). Without this, useQuery drops
    // `data` to `undefined` on a failed refetch, which flips every consumer's
    // "no active plan" branch and renders the red expiry banner — even though
    // the user's subscription is fine. The next successful refetch heals it,
    // but until then the banner is a false positive. See `isError` below for
    // the matching consumer-side gate.
    placeholderData: keepPreviousData,
    // Don't retry on auth errors — refresh-token flow handles those elsewhere.
    retry: (failureCount, error) => {
      const msg = (error as Error)?.message || '';
      if (/unauthor|401|forbidden|403/i.test(msg)) return false;
      return failureCount < 2;
    },
    ...(refetchInterval !== undefined && { refetchInterval }),
  });
}
